import React, {
  PropsWithChildren,
  MouseEvent,
  useMemo,
  isValidElement,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import forIn from 'lodash/forIn';
import mapValues from 'lodash/mapValues';
import {
  formatMoney,
} from '@populateui/utils';
import Tag from 'core/components/Tag';

const predicateNames = ['positive', 'negative', 'neutral'] as const;
type PredicateKey = typeof predicateNames[number];

const parts = ['root', ...predicateNames] as const;
type Part = typeof parts[number];

export type Props = PropsWithChildren & {
  value?: number;
  useColors?: boolean;
  classes?: Partial<Record<Part, string>>;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  tooltip?: string;
  conditionalValue?: number;
};

export const defaultPositiveClass = 'bg-transparent text-green-600 !border-0';
export const defaultNegativeClass = 'bg-transparent text-red-600 !border-0';
export const defaultNeutralClass = 'bg-transparent !text-gray-400 !border-0';

type Predicate = (value: number) => boolean;

const conditions: Readonly<Record<PredicateKey, Predicate>> = Object.freeze({
  neutral: (value: number) => value === 0,
  positive: (value: number) => value > 0,
  negative: (value: number) => value < 0,
});

export default function AmountTag({
  value = 0,
  useColors = false,
  classes,
  children,
  onClick,
  tooltip,
  conditionalValue: _conditionalValue,
}: Props) {
  const computedClasses = useMemo(() => {
    const result: Record<string, Predicate[]> = {};

    if (useColors) {
      forIn(classes, (className, key) => {
        const predicate = conditions[key as PredicateKey];
        if (isNil(predicate) || isNil(className)) return;

        if (typeof result[className] === 'undefined') {
          result[className] = [];
        }

        result?.[className]?.push(conditions[key as PredicateKey]);
      });
    }

    return result;
  }, [classes, useColors]);

  const conditionalValue = _conditionalValue ?? value;

  return (
    <Tag
      tooltip={tooltip}
      tagName="amountTag"
      onClick={onClick}
      className={clsx(
        'font-semibold flex gap-1 !bg-transparent !p-0',
        classes?.root,
        { [defaultNeutralClass]: conditionalValue === 0 && isNil(classes?.neutral) },
        useColors
          ? {
            [defaultPositiveClass]: conditionalValue > 0 && isNil(classes?.positive),
            [defaultNegativeClass]: conditionalValue < 0 && isNil(classes?.negative),
            ...(mapValues(computedClasses, (conditions) => (
              conditions?.some((predicate) => predicate(conditionalValue))
            ))),
          } : {},
      )}
    >
      {isValidElement(children) ? children : formatMoney(value)}
    </Tag>
  );
}
